import type { GetRecurringChargesQuery } from "@/src/gql/graphql";
import type { RecurringCharge } from "@/lib/types/recurring-charges";
import type { RecurringChargeMetadata } from "@west-tennessee-consulting/quickbooks-invoice/types/recurring-charges";

type RecurringChargesCollection = NonNullable<
  GetRecurringChargesQuery["recurring_chargesCollection"]
>;

export type RecurringChargeGraphQLEdge = RecurringChargesCollection["edges"][number];

export type RecurringChargeGraphQLNode = RecurringChargeGraphQLEdge["node"];

export type RecurringChargeNodeLike = Pick<
  RecurringChargeGraphQLNode,
  | "id"
  | "customer_id"
  | "schedule_type"
  | "last_charged_at"
  | "next_charge_date"
  | "metadata"
  | "realm_id"
  | "admin_email"
  | "created_at"
  | "updated_at"
>;

export type RecurringChargeScheduleType = RecurringCharge["schedule_type"];

const SCHEDULE_TYPES: readonly RecurringChargeScheduleType[] = ["monthly", "weekly", "daily"];

function parseScheduleType(value: string): RecurringChargeScheduleType {
  if (SCHEDULE_TYPES.includes(value as RecurringChargeScheduleType)) {
    return value as RecurringChargeScheduleType;
  }
  return "monthly";
}

function parseMetadata(value: RecurringChargeNodeLike["metadata"]): RecurringChargeMetadata {
  if (!value) {
    return {} as RecurringChargeMetadata;
  }

  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as RecurringChargeMetadata;
      }
    } catch {
      return {} as RecurringChargeMetadata;
    }
  }

  return value as RecurringChargeMetadata;
}

export function mapRecurringChargeNode(node: RecurringChargeNodeLike): RecurringCharge {
  return {
    id: node.id,
    customer_id: node.customer_id,
    schedule_type: parseScheduleType(node.schedule_type),
    last_charged_at: node.last_charged_at ?? undefined,
    next_charge_date: node.next_charge_date,
    metadata: parseMetadata(node.metadata),
    realm_id: node.realm_id,
    admin_email: node.admin_email,
    created_at: node.created_at ?? new Date().toISOString(),
    updated_at: node.updated_at ?? new Date().toISOString(),
  };
}

export function mapRecurringChargeEdges(
  edges: RecurringChargesCollection["edges"] | undefined | null
): RecurringCharge[] {
  if (!edges) {
    return [];
  }

  return edges
    .map((edge) => edge?.node)
    .filter((node): node is RecurringChargeGraphQLNode => node != null)
    .map(mapRecurringChargeNode);
}
